﻿import type {StatisticsSet} from "../types/statistics/statistics-set.type";
import {StatisticSentiment} from "../types/statistics/statistic-sentiment.type";
import {
    MEASUREMENT_DURATION_FIELDS,
    MEASUREMENT_FIELD_DELAY_DURATION,
    MEASUREMENT_FIELD_DELAY_FACTOR,
    MEASUREMENT_FIELD_SPEED_DIFFERENCE
} from "../constants/measurement-table-constants";
import {formatDuration} from "../utils/style";
import {getMeasurementFieldDescription, translate} from "../i18n/translate";

const calculatedMeasurementProperties = [
    MEASUREMENT_FIELD_DELAY_FACTOR,
    MEASUREMENT_FIELD_DELAY_DURATION,
    MEASUREMENT_FIELD_SPEED_DIFFERENCE
];

export function getAverage(values: number[]): number {
    return values.reduce((sum, value) => sum + value, 0) / values.length;
}

export function getMedian(values: number[]): number {
    const sorted = [...values].sort((a, b) => a - b);
    const middle = Math.floor(sorted.length / 2);

    if (sorted.length % 2 === 0) {
        return (sorted[middle - 1] + sorted[middle]) / 2;
    }

    return sorted[middle];
}

export function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
}

export function getStatisticsSet(propertyName: string, values: number[]): StatisticsSet {
    const average = getAverage(values);
    const set: StatisticsSet = {
        propertyName: propertyName,
        description: getMeasurementFieldDescription(propertyName),
        average: {
            label: translate('pages.dashboard.AVERAGE'),
            value: `${ formatStatisticValue(average, propertyName) }`,
            sentiment: StatisticSentiment.Neutral
        }
    }
    
    // Avoid spread operator in .Min()/.Max() because it can lead to 'Maximum call stack size exceeded' error
    const median = getMedian(values);
    const min = values.reduce((min, value) => value < min ? value : min, Infinity);
    const max = values.reduce((max, value) => value > max ? value : max, -Infinity);
    
    return { 
        ...set,
        median: {
            label: translate('pages.dashboard.MEDIAN'),
            value: `${ formatStatisticValue(getMedian(values), propertyName) }`,
            sentiment: getSentimentForValue(median, propertyName, average)
        },
        min: {
            label: translate('pages.dashboard.MIN'),
            value: `${ formatStatisticValue(min, propertyName) }`,
            sentiment: getSentimentForValue(min, propertyName, average)
        },
        max: {
            label: translate('pages.dashboard.MAX'),
            value: `${ formatStatisticValue(max, propertyName) }`,
            sentiment: getSentimentForValue(max, propertyName, average)
        }
    }
}

function formatStatisticValue(value: number, propertyName: string): string {
    if(value == Infinity || value == -Infinity || isNaN(value))
        return translate('pages.dashboard.NO_VALUE');
    
    if(MEASUREMENT_DURATION_FIELDS.includes(propertyName))
        return formatDuration(Math.round(value))
    
    return value.toFixed(2);
}

function getSentimentForValue(value: number, propertyName: string, average: number): StatisticSentiment {
    if(value == Infinity || value == -Infinity || isNaN(value))
        return StatisticSentiment.Neutral;

    if(calculatedMeasurementProperties.includes(propertyName))
        return value <= 0 
            ? StatisticSentiment.Positive 
            : StatisticSentiment.Negative;
    
    return value <= average 
        ? StatisticSentiment.Positive 
        : StatisticSentiment.Negative;
}
    